import {otsBaseApi} from "../../ots-api";
import {
  DispatchRequest,
  DispatchRequestDispatch,
  DispatchRequestReassign,
  DispatchRequestReject,
  DispatchRequestSearchCriteria,
  dispatchRequestStatus
} from "../../../model/dispatch-request";
import {DispatchRequestTrackingResult} from "../../../model/dispatch-tracking";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["DispatchRequests"]});

const dispatchRequestApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllDispatchRequests: builder.query<DispatchRequest[], string | undefined>({
      query: (regionId) => ({
        url: `/dispatch-requests/all`,
        method: "GET",
        params: regionId ? {regionId: regionId} : undefined
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "DispatchRequests", id} as const)),
            {type: "DispatchRequests", id: "LIST"}
          ];
        } else {
          return [{type: "DispatchRequests", id: "LIST"}];
        }
      }
    }),
    searchDispatchRequests: builder.query<
      DispatchRequest[],
      DispatchRequestSearchCriteria
    >({
      query: (criteria) => ({
        url: "/dispatch-requests/search",
        method: "POST",
        body: criteria
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "DispatchRequests", id} as const)
            ),
            {type: "DispatchRequests", id: "LIST"}
          ];
        } else {
          return [{type: "DispatchRequests", id: "LIST"}];
        }
      }
    }),
    getPendingDispatchRequests: builder.query<
      DispatchRequest[],
      { regionId: string, fromDttm?: number }
    >({
      query: ({regionId, fromDttm}) => ({
        url: "/dispatch-requests/search",
        method: "POST",
        body: {
          statuses: [dispatchRequestStatus.pending.code],
          regionId: regionId,
          fromDttm: fromDttm,
        }
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "DispatchRequests", id} as const)
            ),
            {type: "DispatchRequests", id: "LIST"}
          ];
        } else {
          return [{type: "DispatchRequests", id: "LIST"}];
        }
      }
    }),
    getUnauthorizedDispatchRequests: builder.query<
      DispatchRequest[],
      string
    >({
      query: (regionId) => ({
        url: "/dispatch-requests/search",
        method: "POST",
        body: {
          statuses: [dispatchRequestStatus.pending.code],
          authorized: false,
          regionId: regionId
        }
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "DispatchRequests", id} as const)
            ),
            {type: "DispatchRequests", id: "LIST"}
          ];
        } else {
          return [{type: "DispatchRequests", id: "LIST"}];
        }
      }
    }),
    getDispatchRequest: builder.query<DispatchRequest, string>({
      query: (id) => ({
        url: `/dispatch-requests/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "DispatchRequests", id}]
    }),
    getDispatchRequestTracking: builder.query<DispatchRequestTrackingResult, string>({
      query: (id) => ({
        url: `/dispatch-requests/${id}/tracking`,
        method: "GET",
        params: {
          timeFormat: "epoch"
        }
      }),
      providesTags: (result, error, id) => [{type: "DispatchRequests", id}]
    }),
    createDispatchRequest: builder.mutation<DispatchRequest, Partial<DispatchRequest>>({
      query: (arg) => ({
        url: "/dispatch-requests",
        method: "POST",
        body: arg
      }),
      invalidatesTags: [{type: "DispatchRequests", id: "LIST"}]
    }),
    updateDispatchRequest: builder.mutation<DispatchRequest, Partial<DispatchRequest>>({
      query: (arg) => ({
        url: "/dispatch-requests",
        method: "PUT",
        body: arg
      }),
      invalidatesTags: (result, error, {id}) =>
        [{type: "DispatchRequests", id}, {type: 'DispatchRequests', id: 'LIST'}]
    }),
    acceptDispatchRequest: builder.mutation<DispatchRequest, DispatchRequestDispatch>({
      query: (arg) => ({
        url: "/dispatch-requests/accept",
        method: "PUT",
        body: arg
      }),
      invalidatesTags: (result, error, {dispatchRequestId}) =>
        [{type: "DispatchRequests", dispatchRequestId}, {type: 'DispatchRequests', id: 'LIST'}]
    }),
    rejectDispatchRequest: builder.mutation<DispatchRequest, DispatchRequestReject>({
      query: (arg) => ({
        url: "/dispatch-requests/reject",
        method: "PUT",
        body: arg
      }),
      invalidatesTags: (result, error, {dispatchRequestId}) =>
        [{type: "DispatchRequests", dispatchRequestId}, {type: 'DispatchRequests', id: 'LIST'}]
    }),
    addDispatchToDispatchRequest: builder.mutation<DispatchRequest, DispatchRequestDispatch>({
      query: (arg) => ({
        url: "/dispatch-requests/add-dispatch",
        method: "PUT",
        body: arg
      }),
      invalidatesTags: (result, error, {dispatchRequestId}) =>
        [{type: "DispatchRequests", dispatchRequestId}, {type: 'DispatchRequests', id: 'LIST'}]
    }),
    reassignDispatchRequest: builder.mutation<DispatchRequest, DispatchRequestReassign>({
      query: (arg) => ({
        url: "/dispatch-requests/reassign",
        method: "POST",
        body: arg
      }),
      invalidatesTags: (result, error, {dispatchRequestId}) =>
        [{type: "DispatchRequests", dispatchRequestId}, {type: 'DispatchRequests', id: 'LIST'}]
    }),
    deleteDispatchRequest: builder.mutation<void, Partial<DispatchRequest>>({
      query: (arg) => ({
        url: "/dispatch-requests",
        method: "DELETE",
        body: arg
      }),
      invalidatesTags: (result, error, {id}) =>
        [{type: "DispatchRequests", id}, {type: 'DispatchRequests', id: 'LIST'}]
    })
  })
});

const usePrefetch = dispatchRequestApi.usePrefetch;

export {usePrefetch as useDispatchRequestPrefetch};

export const {
  useGetAllDispatchRequestsQuery,
  useSearchDispatchRequestsQuery,
  useGetPendingDispatchRequestsQuery,
  useGetUnauthorizedDispatchRequestsQuery,
  useGetDispatchRequestQuery,
  useGetDispatchRequestTrackingQuery,
  useCreateDispatchRequestMutation,
  useUpdateDispatchRequestMutation,
  useAcceptDispatchRequestMutation,
  useRejectDispatchRequestMutation,
  useAddDispatchToDispatchRequestMutation,
  useReassignDispatchRequestMutation,
  useDeleteDispatchRequestMutation
} = dispatchRequestApi;
